import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '@customer-portal/environments';

import { SKIP_CUSTOM_HEADERS } from './maintenance.service';

interface ValidateBypassResponse {
  isValid: boolean;
  reason?: string;
  expiresAt?: string;
}

/**
 * Service to handle maintenance bypass functionality for the Customer Portal
 *
 * Allows authorized users to access the site during maintenance using
 * time-limited cryptographically signed bypass tokens.
 */
@Injectable({
  providedIn: 'root',
})
export class MaintenanceBypassService {
  private readonly http = inject(HttpClient);

  private readonly BYPASS_KEY = 'maintenance_bypass';
  private readonly BYPASS_VERIFIED_KEY = 'maintenance_bypass_verified_at';
  private readonly BYPASS_TOKEN_KEY = 'maintenance_bypass_token';
  private readonly BYPASS_VALIDITY_HOURS = 1;

  constructor() {
    (window as any).clearMaintenanceBypass = () => {
      this.clearBypass();
    };
  }

  async checkBypassToken(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('bypass');

    if (!token) {
      return;
    }

    const storedToken = sessionStorage.getItem(this.BYPASS_TOKEN_KEY);
    const url = new URL(window.location.href);
    url.searchParams.delete('bypass');
    window.history.replaceState({}, '', url);

    if (storedToken === token && this.isBypassed()) {
      return;
    }

    this.clearBypass();

    try {
      const result = await firstValueFrom(
        this.http.post<ValidateBypassResponse>(
          `${environment.maintenance.functionsBaseUrl}/bypass/validate`,
          { token },
          {
            headers: {
              'Ocp-Apim-Subscription-Key':
                environment.maintenance.apimSubscriptionMaintenanceKey,
            },
            context: new HttpContext().set(SKIP_CUSTOM_HEADERS, true),
            withCredentials: true,
          },
        ),
      );

      if (result.isValid) {
        sessionStorage.setItem(this.BYPASS_KEY, 'true');
        sessionStorage.setItem(
          this.BYPASS_VERIFIED_KEY,
          new Date().toISOString(),
        );
        sessionStorage.setItem(this.BYPASS_TOKEN_KEY, token);
      } else {
        this.clearBypass();
      }
    } catch {
      this.clearBypass();
    }
  }

  isBypassed(): boolean {
    const bypassActive = sessionStorage.getItem(this.BYPASS_KEY) === 'true';
    const verifiedAt = sessionStorage.getItem(this.BYPASS_VERIFIED_KEY);

    if (!bypassActive || !verifiedAt) {
      return false;
    }

    const verifiedTime = new Date(verifiedAt);
    const now = new Date();
    const hoursSinceVerification =
      (now.getTime() - verifiedTime.getTime()) / (1000 * 60 * 60);

    if (hoursSinceVerification > this.BYPASS_VALIDITY_HOURS) {
      this.clearBypass();

      return false;
    }

    return true;
  }

  clearBypass(): void {
    sessionStorage.removeItem(this.BYPASS_KEY);
    sessionStorage.removeItem(this.BYPASS_VERIFIED_KEY);
    sessionStorage.removeItem(this.BYPASS_TOKEN_KEY);
  }
}
